import React from 'react';
import PropTypes from 'prop-types';

import Status from 'components/status';
import Icon from 'components/icon';
import Link from 'components/link';
import ButtonLink from 'components/button/link';
import Alert from 'components/alert';
import DropdownButton from 'components/button/dropdown';

import './storefront.scss';

export default class VersionDropdown extends React.PureComponent {
  static contextTypes = {
    notifyError: PropTypes.func.isRequired,
  };

  static propTypes = {
    name: PropTypes.string.isRequired,
    storefront: PropTypes.object.isRequired,
    storefronts: PropTypes.object.isRequired,
    appVersion: PropTypes.string,
    installedApp: PropTypes.object,
    installedApps: PropTypes.object.isRequired,
    onSubmitUpdate: PropTypes.func.isRequired,
    storefrontUpdates: PropTypes.object.isRequired,
    fetchUpgradeConflicts: PropTypes.func.isRequired,
  };

  state = {
    open: false,
    loaded: false,
    loading: false,
    conflicts: null,
  };

  mounted = false;

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    const { storefront } = this.props;
    if (prevProps.storefront !== storefront) {
      this.setState({ loaded: false, conflicts: null });
    }
  }

  getThemeUpdateOptions() {
    const { storefront, storefrontUpdates } = this.props;

    const updateOptions = storefrontUpdates[storefront.id]?.[0];
    if (updateOptions?.themeVersion) {
      return updateOptions;
    }

    return null;
  }

  shouldShowUpdateBadge(updateOptions) {
    const { storefront, storefronts, installedApp } = this.props;

    if (!updateOptions?.themeVersion || installedApp?.version === null) {
      return false;
    }

    const clonedStorefront = storefronts.results.find(
      (sf) => sf.cloned_updated_id === storefront.id,
    );

    if (
      clonedStorefront &&
      updateOptions.themeVersion !== clonedStorefront.theme_version
    ) {
      return false;
    }

    return true;
  }

  onChangeOpen = (open) => {
    this.setState({ open });
    if (open) {
      this.loadUpgradeConflicts();
    }
  };

  loadUpgradeConflicts = async (optionSelected = 0) => {
    const { fetchUpgradeConflicts, storefront, storefrontUpdates } = this.props;

    let conflicts = null;

    const updateOptions = storefrontUpdates[storefront.id];

    if (updateOptions) {
      const { themeVersion } = updateOptions[optionSelected || 0];

      this.setState({ loading: true });

      conflicts = await fetchUpgradeConflicts(
        storefront,
        themeVersion?.version,
      );
    }

    if (this.mounted) {
      this.setState({ loaded: true, loading: false, conflicts });
    }
  };

  onClickUpdate = async (event) => {
    event.preventDefault();
    const { onSubmitUpdate, storefront, storefrontUpdates } = this.props;
    const updateOptions = storefrontUpdates[storefront.id];
    const { themeVersion } = updateOptions[0];

    const result = await onSubmitUpdate({
      theme_version: themeVersion.version,
    });
    if (result?.errors) {
      this.context.notifyError(result.errors);
    } else {
      this.setState({ open: false });
    }
  };

  onClickViewReleaseNotes = (appId, event) => {
    event.preventDefault();
    this.props.router.push(`/apps/${appId}/?tab=versions`);
  };

  getUpdateDialog() {
    const { loaded, conflicts } = this.state;
    const { storefront, installedApp } = this.props;
    const { theme, theme_app_id } = storefront;

    if (!loaded) {
      return {
        loading: true,
      };
    }

    const isDevApp = installedApp?.version === null;
    const updateOptions = !isDevApp && this.getThemeUpdateOptions();
    const { themeApp, themeVersion } = updateOptions || {};
    const isUpdateAvailable = Boolean(updateOptions);

    return {
      title: isUpdateAvailable
        ? `Update to ${themeApp.app.name} ${themeVersion.version}`
        : `This ${theme_app_id ? 'theme' : 'app'} is up to date`,
      subtitle: isUpdateAvailable && `Current version ${theme.version}`,
      width: 450,
      children: isUpdateAvailable ? (
        <>
          <p>
            A copy of your storefront will be created with an updated{' '}
            {themeApp ? 'theme' : 'app'} version so you can review changes
            before publishing. The copy will include your customized{' '}
            <b>content and settings</b>.
          </p>
          {conflicts && (
            <Alert type="info">
              Your <b>code changes</b> won't be included because one or more
              changes conflict with the update. {/* TODO: View conflicts*/}
            </Alert>
          )}
        </>
      ) : (
        <p>
          {theme.name} {theme.version} is{' '}
          {installedApp?.version === null
            ? `a development version.`
            : 'the latest version.'}
        </p>
      ),
      actions: [
        ...(isUpdateAvailable
          ? [
              {
                label: 'Add updated storefront',
                type: 'default',
                size: 'sm',
                onClick: this.onClickUpdate,
              },
              {
                component: (
                  <Link
                    onClick={this.onClickViewReleaseNotes.bind(
                      this,
                      theme_app_id,
                    )}
                    className="as-link"
                  >
                    View release notes
                  </Link>
                ),
              },
            ]
          : [
              <ButtonLink
                type="secondary"
                size="sm"
                onClick={this.onClickViewReleaseNotes.bind(this, theme_app_id)}
              >
                View release notes
              </ButtonLink>,
            ]),
      ],
    };
  }

  render() {
    const { name, appVersion, installedApp } = this.props;

    const installedVersion =
      appVersion || installedApp?.version || installedApp?.app?.version;

    const updateOptions = this.getThemeUpdateOptions();
    const shouldShowUpdateBadge = this.shouldShowUpdateBadge(updateOptions);

    return (
      <div className="storefront-version-dropdown">
        <DropdownButton
          button={false}
          arrow={false}
          align="start"
          alignOffset={0}
          open={this.state.open}
          dialog={this.getUpdateDialog()}
          onChangeOpen={this.onChangeOpen}
        >
          <Status type="primary" size="sm" dot={false} tag={false}>
            {name} {installedVersion} <Icon fa="chevron-down" />
            {shouldShowUpdateBadge && (
              <Status
                type="primary"
                dot={false}
                className="storefront-version-dropdown-available"
              >
                Update available
              </Status>
            )}
          </Status>
        </DropdownButton>
      </div>
    );
  }
}
